import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Sparkles,
  ArrowRight,
  ArrowLeft,
  Loader2,
  MessageCircle,
  Send,
  FileText,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Scale,
  Building2,
  Briefcase,
  Home,
  Users,
  HelpCircle,
  Target,
  Lightbulb
} from 'lucide-react';

const ContractRecommendation = ({ onSelectContract }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  
  const [step, setStep] = useState('questionnaire'); // questionnaire, results, chat
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [recommendations, setRecommendations] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  useEffect(() => {
    fetchQuestionnaire();
  }, []);

  const fetchQuestionnaire = async () => {
    try {
      const response = await api.get('/recommend/questionnaire');
      setQuestions(response.data.questions || []);
    } catch (error) {
      console.error('Error fetching questionnaire:', error);
      toast.error('Error al cargar el cuestionario');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      getRecommendations();
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const getRecommendations = async () => {
    setLoading(true);
    try {
      const response = await api.post('/recommend', {
        answers,
        max_results: 10
      });
      setRecommendations(response.data);
      setStep('results');
      
      // Initialize chat with AI advice
      if (response.data.general_advice) {
        setChatMessages([{
          role: 'assistant',
          content: response.data.general_advice
        }]);
      }
    } catch (error) {
      console.error('Error getting recommendations:', error);
      toast.error('Error al obtener recomendaciones');
    } finally {
      setLoading(false);
    }
  };

  const handleChatSubmit = async () => {
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);

    try {
      const response = await api.post('/recommend/refine', {
        message: userMessage,
        current_recommendations: recommendations?.recommendations || [],
        chat_history: chatMessages
      });

      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response 
      }]);

      // Update recommendations if changed
      if (response.data.recommendations) {
        setRecommendations(prev => ({
          ...prev,
          recommendations: response.data.recommendations
        }));
      }
    } catch (error) {
      console.error('Error in chat:', error);
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Lo siento, hubo un error. Por favor intenta de nuevo.' 
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleSelectRecommendation = (templateId) => {
    if (onSelectContract) {
      onSelectContract(templateId);
    } else {
      navigate(`/discovery/${templateId}`);
    }
  };

  const getIconForSituation = (situation) => {
    const icons = {
      personal: Users,
      business: Building2,
      employment: Briefcase,
      property: Home,
      legal_dispute: Scale,
      other: HelpCircle
    };
    return icons[situation] || HelpCircle;
  };

  const question = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const currentAnswer = question ? answers[question.id] : null;

  if (loading && step === 'questionnaire') {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  // Results view with recommendations
  if (step === 'results' && recommendations) {
    return (
      <div className="space-y-6" data-testid="recommendation-results">
        {/* Header */}
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold font-['Outfit'] mb-2">
            Contratos Recomendados para Ti
          </h2>
          <p className="text-slate-400">
            Basado en tu situación, estos son los contratos más relevantes
          </p>
        </div>

        {/* Warnings */}
        {recommendations.warnings?.length > 0 && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-amber-400">Importante</h4>
                {recommendations.warnings.map((warning, i) => (
                  <p key={i} className="text-sm text-amber-300/80">{warning}</p>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Recommendations Grid */}
        <div className="grid gap-4">
          {recommendations.recommendations?.map((rec, index) => (
            <div
              key={rec.id}
              className="bg-card border border-border rounded-xl p-4 hover:border-blue-500/50 transition-all cursor-pointer group"
              onClick={() => handleSelectRecommendation(rec.id)}
              data-testid={`recommendation-${rec.id}`}
            >
              <div className="flex items-start gap-4">
                {/* Rank */}
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  index === 0 ? 'bg-gradient-to-br from-yellow-500 to-amber-600' :
                  index === 1 ? 'bg-gradient-to-br from-slate-400 to-slate-500' :
                  index === 2 ? 'bg-gradient-to-br from-amber-700 to-amber-800' :
                  'bg-slate-700'
                }`}>
                  <span className="text-white font-bold">{index + 1}</span>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="font-semibold text-lg group-hover:text-blue-400 transition-colors">
                      {rec.name}
                    </h3>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {rec.relevance_score && (
                        <span className="text-xs px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full">
                          {rec.relevance_score}% relevante
                        </span>
                      )}
                      <ArrowRight className="w-5 h-5 text-slate-500 group-hover:text-blue-400 group-hover:translate-x-1 transition-all" />
                    </div>
                  </div>
                  
                  {rec.ai_reason && (
                    <div className="flex items-start gap-2 mb-2">
                      <Lightbulb className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-slate-300">{rec.ai_reason}</p>
                    </div>
                  )}
                  
                  <p className="text-sm text-slate-400 mb-3">{rec.description}</p>
                  
                  <div className="flex items-center gap-4 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {rec.estimated_time}
                    </span>
                    <span className={`px-2 py-0.5 rounded ${
                      rec.complexity === 'simple' ? 'bg-green-500/20 text-green-400' :
                      rec.complexity === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {rec.complexity === 'simple' ? 'Simple' : rec.complexity === 'medium' ? 'Medio' : 'Complejo'}
                    </span>
                    {rec.requires_notary && (
                      <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded">
                        Requiere notaría
                      </span>
                    )}
                  </div>

                  {rec.ai_tip && (
                    <p className="text-xs text-blue-400 mt-2 italic">{rec.ai_tip}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Chat Refinement */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="p-4 border-b border-border flex items-center gap-3">
            <MessageCircle className="w-5 h-5 text-blue-400" />
            <span className="font-medium">¿Necesitas ayuda para elegir?</span>
          </div>
          
          <div className="h-48 overflow-y-auto p-4 space-y-3">
            {chatMessages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-lg p-3 text-sm ${
                  msg.role === 'user' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-slate-800 text-slate-200'
                }`}>
                  {msg.content}
                </div>
              </div>
            ))}
            {chatLoading && (
              <div className="flex justify-start">
                <div className="bg-slate-800 rounded-lg p-3">
                  <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
                </div>
              </div>
            )}
          </div>
          
          <div className="p-3 border-t border-border flex gap-2">
            <input
              type="text"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleChatSubmit()}
              placeholder="Pregunta algo sobre los contratos..."
              className="flex-1 px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg focus:border-blue-500 text-sm"
            />
            <button
              onClick={handleChatSubmit}
              disabled={!chatInput.trim() || chatLoading}
              className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 disabled:opacity-50"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={() => {
              setStep('questionnaire');
              setCurrentQuestion(0);
              setAnswers({});
              setRecommendations(null);
              setChatMessages([]);
            }}
            className="flex-1 py-3 border border-slate-600 rounded-xl hover:bg-slate-800 transition-colors"
          >
            Empezar de Nuevo
          </button>
          <button
            onClick={() => navigate('/templates')}
            className="flex-1 py-3 bg-slate-700 rounded-xl hover:bg-slate-600 transition-colors"
          >
            Ver Todos los Contratos
          </button>
        </div>
      </div>
    );
  }

  // Questionnaire view
  return (
    <div className="max-w-2xl mx-auto" data-testid="recommendation-questionnaire">
      {/* Progress */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-slate-400">
            Pregunta {currentQuestion + 1} de {questions.length}
          </span>
          <span className="text-sm text-blue-400">{Math.round(progress)}%</span>
        </div>
        <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Question */}
      {question && (
        <div className="space-y-6">
          <div className="text-center">
            <Target className="w-12 h-12 text-blue-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold font-['Outfit'] mb-2">
              {question.question}
            </h2>
          </div>

          {/* Options */}
          {question.type === 'select' && (
            <div className="grid gap-3">
              {question.options?.map((option) => {
                const isSelected = currentAnswer === option.value;
                return (
                  <button
                    key={option.value}
                    onClick={() => handleAnswer(question.id, option.value)}
                    className={`p-4 rounded-xl border text-left transition-all ${
                      isSelected 
                        ? 'border-blue-500 bg-blue-500/10' 
                        : 'border-border hover:border-slate-600'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        isSelected ? 'border-blue-500 bg-blue-500' : 'border-slate-600'
                      }`}>
                        {isSelected && <CheckCircle2 className="w-3 h-3 text-white" />}
                      </div>
                      <span className={isSelected ? 'text-white' : 'text-slate-300'}>
                        {option.label}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Textarea */}
          {question.type === 'textarea' && (
            <textarea
              value={currentAnswer || ''}
              onChange={(e) => handleAnswer(question.id, e.target.value)}
              placeholder={question.placeholder || 'Escribe tu respuesta...'}
              rows={4}
              className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-4 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all resize-none"
            />
          )}

          {/* Navigation */}
          <div className="flex gap-3 pt-4">
            {currentQuestion > 0 && (
              <button
                onClick={handleBack}
                className="flex items-center gap-2 px-6 py-3 border border-slate-600 rounded-xl hover:bg-slate-800 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Anterior
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={!currentAnswer && question.type === 'select'}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {currentQuestion < questions.length - 1 ? (
                <>
                  Siguiente
                  <ArrowRight className="w-4 h-4" />
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Obtener Recomendaciones
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractRecommendation;
